import axiosInstance from "core/api/axiosInstance";
import * as XLSX from "xlsx";

export const TAGS_QUERY_TIMEOUT_MS = 120000;
export const TAGS_IMPORT_TIMEOUT_MS = 180000;
const workbooks = new WeakMap();
const IDENTIFIER_HEADERS = new Set([
  "Reverse Number",
  "ReverseNumber",
  "reverse_number",
  "Reverse",
  "reverse number",
  "Number Below the QR",
  "NumberBelowQR",
  "number_below_qr",
  "QR",
  "Qr",
  "qr",
  "Code QR",
  "CodeQR",
  "code_qr",
  "code",
  "CodigoQR",
  "Código QR",
]);

/**
 * Formatea una fecha para mostrar
 */
const formatDate = (dateString) => {
  if (!dateString) return "Sin información";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Sin información";

  return date.toLocaleString("es-ES", {
    timeZone: "America/Bogota",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

/**
 * Mapea el estado del tag a un color para la UI
 */
const getStatusColor = (status) => {
  const statusMap = {
    "SIN PISTOLEAR": "warning",
    PISTOLEADO: "success",
    ERROR: "error",
    PROCESANDO: "info",
    VALIDADO: "success",
    VERIFICADO: "info",
    ACTIVO: "success",
  };
  return statusMap[status] || "default";
};

/**
 * Mapea el estado del tag a texto legible
 */
const getStatusText = (status) => {
  const statusMap = {
    "SIN PISTOLEAR": "Sin pistolear",
    PISTOLEADO: "Pistoleado",
    ERROR: "Error",
    PROCESANDO: "Procesando",
    VALIDADO: "Validado",
    VERIFICADO: "Verificado",
    ACTIVO: "Activo",
  };
  return statusMap[status] || status;
};

/**
 * Normaliza un tag para la UI
 */
export const normalizeTag = (tag) => {
  const statusColor = getStatusColor(tag.status);
  const statusText = tag.status_tag || getStatusText(tag.status);

  // Usar statusconsole como estado principal para mostrar
  const consoleStatus = tag.statusconsole || tag.status_console || tag.status || "Sin información";

  // Mapear colores para estados de consola
  const getConsoleColor = (status) => {
    const colorMap = {
      Activado: "success",
      Asignado: "info",
      "Con el supervisor": "purple",
      Registrado: "teal",
      "Sin asignar": "warning",
      '"Sin pistolear"': "orange",
      '"sin pistolear"': "orange",
    };
    return colorMap[status] || "default";
  };

  const consoleColor = getConsoleColor(consoleStatus);

  return {
    id: tag.id ?? "N/A",
    reverse_number:
      tag.reverse_number || tag.ReverseNumber || tag.reverseNumber || tag.reverse || "N/A",
    qr: tag.qr || tag.QR || tag.Qr || tag.number_below_qr || tag.numberBelowQR || "N/A",
    code: tag.code || tag.Code || tag.codigo || tag.CodigoQR || tag.code_qr || "Sin información",
    epc: tag.epc || tag.EPC || "N/A",
    status: tag.status || "N/A",
    statusText: statusText,
    statusColor: statusColor,
    statusBadge: {
      color: consoleColor,
      text: consoleStatus,
    },
    statusconsole: consoleStatus, // Guardamos el statusconsole
    lot: tag.lot ?? tag.LOTE ?? tag.Lote ?? null,
    userId: tag.user_id || tag.userId || tag.userID || "N/A",
    more: tag.more || null,
    is_active: [true, 1, "1", "true"].includes(tag.is_active),
    is_verified: [true, 1, "1", "true"].includes(tag.is_verified),
    createdAt: tag.createdAt || tag.created_at || null,
    createdAtFormatted: formatDate(tag.createdAt || tag.created_at),
    updatedAt: tag.updatedAt || tag.updated_at,
    updatedAtFormatted: formatDate(tag.updatedAt || tag.updated_at),
    deletedAt: tag.deletedAt || tag.deleted_at,
    rawData: tag,
  };
};

/**
 * Obtiene tags paginados desde el servidor
 * @param {Object} params - Parámetros de paginación
 * @param {number} params.offset - Offset para paginación
 * @param {number} params.limit - Límite de registros por página
 * @param {number} params.lot - Número de lote (opcional)
 * @param {string} params.status - Estado del tag (opcional)
 */
export const getTagsPaginated = async (params = {}) => {
  const { offset = 0, limit = 100, lot = null, status = null, signal } = params;

  try {
    let url = `/api/automatization/tag/get-tags?offset=${offset}&limit=${limit}`;
    if (lot) {
      url += `&lot=${lot}`;
    }
    if (status && status !== "todos") {
      url += `&status=${encodeURIComponent(status)}`;
    }

    const { data } = await axiosInstance.get(url, {
      timeout: TAGS_QUERY_TIMEOUT_MS,
      signal,
    });

    const isSuccessful = data?.msm === "succesful" || data?.success === true;

    if (!isSuccessful) {
      return {
        success: false,
        error: data.data?.message || data.message || "Error al obtener tags",
        data: [],
        total: 0,
        offset: 0,
        limit: limit,
        stats: {},
        consoleStats: [],
      };
    }

    // Extraer los items y el total de la respuesta
    let items = [];
    let total = 0;
    let stats = {};
    let consoleStats = [];

    if (data.data && data.data.items && Array.isArray(data.data.items)) {
      items = data.data.items;
      total = parseInt(data.data.count) || 0;

      // Extraer estadísticas
      stats = {
        pistoleados: parseInt(data.data.pistoleados) || 0,
        sinPistolear: parseInt(data.data.sinPistolear) || 0,
        verificado: parseInt(data.data.verificado) || 0,
        activo: parseInt(data.data.activo) || 0,
        total: total,
      };

      // Extraer estados de consola
      if (data.data.estadosConsola && Array.isArray(data.data.estadosConsola)) {
        consoleStats = data.data.estadosConsola.map((item) => ({
          status_console: item.status_console || "Desconocido",
          count_status: item.count_status || "0",
        }));
      }
    } else if (data.data && Array.isArray(data.data)) {
      items = data.data;
      total = items.length;
    } else if (data.tags && Array.isArray(data.tags)) {
      items = data.tags;
      total = items.length;
    }

    const formattedTags = items.map(normalizeTag);

    return {
      success: true,
      data: formattedTags,
      total: total,
      offset: offset,
      limit: limit,
      totalKnown: data.data?.count !== undefined,
      hasMore: offset + items.length < total,
      stats: stats,
      consoleStats: consoleStats, // <-- Estados de consola
      message: data.msm || "Tags obtenidos exitosamente",
    };
  } catch (error) {
    if (!signal?.aborted) console.error("Error al obtener tags paginados:", error);

    if (
      ["ECONNABORTED", "ETIMEDOUT"].includes(error.code) ||
      /timeout/i.test(error.message || "")
    ) {
      return {
        success: false,
        error: "El servidor no respondió en 2 minutos. Puedes reintentar la consulta.",
        data: [],
        total: 0,
        offset: 0,
        limit: limit,
        stats: {},
        consoleStats: [],
      };
    }

    return {
      success: false,
      error: [408, 504].includes(error.response?.status)
        ? "El servidor o proxy agotó su tiempo de espera. Revisa su configuración o reintenta la consulta."
        : error.code === "ERR_NETWORK"
        ? "No se pudo conectar con el servidor de tags. Revisa la conexión y vuelve a intentar."
        : error.message || "Error al obtener tags",
      data: [],
      total: 0,
      offset: 0,
      limit: limit,
      stats: {},
      consoleStats: [],
    };
  }
};

/**
 * Obtiene tags por lote específico
 */
export const getTagsByLot = async (lot, offset = 0, limit = 100) => {
  return getTagsPaginated({ offset, limit, lot });
};

/**
 * Obtiene tags por estado
 */
export const getTagsByStatus = async (status, offset = 0, limit = 100) => {
  return getTagsPaginated({ offset, limit, status });
};

/**
 * Obtiene todos los tags existentes (para validaciones de importación)
 */
export const getAllTags = async ({ lot = null, status = null, signal, onProgress } = {}) => {
  const tags = [];
  const pages = new Set();
  const identifiers = new Set();
  let offset = 0;
  for (let page = 0; page < 10000; page += 1) {
    if (signal?.aborted) throw new DOMException("Consulta cancelada", "AbortError");
    const result = await getTagsPaginated({ offset, limit: 1000, lot, status, signal });
    if (!result.success)
      throw new Error(result.error || "No se pudo completar la consulta de tags");
    const rows = result.data;
    if (!rows.length) {
      if (result.totalKnown && offset < result.total)
        throw new Error("El servidor devolvió un listado incompleto de tags.");
      return { ...result, data: tags, total: tags.length };
    }
    const signature = JSON.stringify(rows.map((tag) => [tag.id, tag.reverse_number, tag.qr]));
    if (pages.has(signature))
      throw new Error("El servidor repite páginas; no se puede garantizar un listado completo.");
    pages.add(signature);
    for (const tag of rows) {
      const key =
        tag.id !== "N/A" ? `id:${tag.id}` : JSON.stringify([tag.reverse_number, tag.qr, tag.code]);
      if (identifiers.has(key))
        throw new Error(
          "El listado cambió durante la consulta o contiene tags repetidos. Actualiza y reintenta."
        );
      identifiers.add(key);
    }
    tags.push(...rows);
    offset += rows.length;
    onProgress?.({ loaded: offset, total: result.totalKnown ? result.total : null });
    if (result.totalKnown && offset >= result.total)
      return { ...result, data: tags, total: tags.length };
  }
  throw new Error("No se pudo completar la paginación de tags.");
};

export const getAllExistingTags = async (options = {}) => {
  const result = await getAllTags(options);
  return result.data.map((tag) => tag.rawData);
};

/**
 * Verifica si un tag ya existe en la base de datos
 */
/**
 * Lee un archivo Excel y devuelve los datos como JSON
 */
const readExcelFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array", cellNF: true });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const range = XLSX.utils.decode_range(firstSheet["!ref"] || "A1");
        for (let column = range.s.c; column <= range.e.c; column += 1) {
          const header = firstSheet[XLSX.utils.encode_cell({ r: range.s.r, c: column })]?.v;
          if (!IDENTIFIER_HEADERS.has(header)) continue;
          for (let row = range.s.r + 1; row <= range.e.r; row += 1) {
            const cell = firstSheet[XLSX.utils.encode_cell({ r: row, c: column })];
            if (cell?.t === "n" && (!Number.isSafeInteger(cell.v) || Math.abs(cell.v) >= 1e15))
              throw new Error(
                `Fila ${row + 1}: guarda ${header} como texto para conservar todos sus dígitos.`
              );
            if (cell?.t === "n") {
              const width = /^0+$/.test(cell.z || "") ? cell.z.length : 0;
              firstSheet[XLSX.utils.encode_cell({ r: row, c: column })] = {
                t: "s",
                v: String(cell.v).padStart(width, "0"),
              };
            }
          }
        }
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { raw: false, defval: "" });
        workbooks.set(file, workbook);
        resolve(jsonData);
      } catch (error) {
        reject(new Error("Error al leer el archivo Excel: " + error.message));
      }
    };

    reader.onerror = () => {
      reject(new Error("Error al leer el archivo"));
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * Valida un archivo Excel de tags antes de importar
 */
export const validateTagsFile = async (file, { signal } = {}) => {
  try {
    if (!file) {
      return {
        isValid: false,
        errors: ["No se ha seleccionado ningún archivo"],
        duplicateInFile: [],
        duplicateInDatabase: [],
        data: [],
      };
    }

    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (fileExtension !== "xlsx" && fileExtension !== "xls") {
      return {
        isValid: false,
        errors: ["Formato de archivo inválido. Solo se permiten archivos .xlsx o .xls"],
        duplicateInFile: [],
        duplicateInDatabase: [],
        data: [],
      };
    }

    const maxSize = 40 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        isValid: false,
        errors: ["El archivo es demasiado grande. Máximo 40MB permitidos"],
        duplicateInFile: [],
        duplicateInDatabase: [],
        data: [],
      };
    }

    const data = await readExcelFile(file);

    if (!data || data.length === 0) {
      return {
        isValid: false,
        errors: ["El archivo no contiene datos válidos"],
        duplicateInFile: [],
        duplicateInDatabase: [],
        data: [],
      };
    }

    const expectedHeadersVariations = {
      "Reverse Number": [
        "Reverse Number",
        "ReverseNumber",
        "reverse_number",
        "reverse number",
        "Reverse",
      ],
      "Number Below the QR": [
        "Number Below the QR",
        "NumberBelowQR",
        "number_below_qr",
        "QR",
        "Qr",
        "qr",
      ],
      "Code QR": ["Code QR", "CodeQR", "code_qr", "code", "CodigoQR", "Código QR"],
      ESTADO: ["ESTADO", "Estado", "estado", "status"],
      LOTE: ["LOTE", "Lote", "lote", "lot"],
      "ESTADO TAGS": ["ESTADO TAGS", "EstadoTags", "estado_tags", "status_tags"],
    };

    const headers = Object.keys(data[0]);
    const foundHeaders = {};
    const missingHeaders = [];

    for (const [required, variations] of Object.entries(expectedHeadersVariations)) {
      const found = variations.find((v) => headers.includes(v));
      if (found) {
        foundHeaders[required] = found;
      } else {
        missingHeaders.push(required);
      }
    }

    if (missingHeaders.length > 0) {
      return {
        isValid: false,
        errors: [`Faltan columnas requeridas: ${missingHeaders.join(", ")}`],
        duplicateInFile: [],
        duplicateInDatabase: [],
        data: [],
      };
    }

    const existingTags = await getAllExistingTags({ signal });
    const existingIndex = {
      reverse_number: new Map(),
      number_below_qr: new Map(),
      code_qr: new Map(),
    };
    for (const tag of existingTags) {
      const fields = {
        reverse_number: tag.reverse_number ?? tag.ReverseNumber ?? tag.reverseNumber ?? tag.reverse,
        number_below_qr: tag.qr ?? tag.QR ?? tag.Qr ?? tag.number_below_qr ?? tag.numberBelowQR,
        code_qr: tag.code ?? tag.Code ?? tag.codigo ?? tag.CodigoQR ?? tag.code_qr,
      };
      for (const [field, value] of Object.entries(fields)) {
        if (value !== undefined && value !== null)
          existingIndex[field].set(String(value).trim(), { id: tag.id, value });
      }
    }

    const errors = [];
    const validRows = [];
    const reverseNumbersInFile = new Map();
    const numbersBelowQRInFile = new Map();
    const qrCodesInFile = new Map();
    const duplicateInFile = [];

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const rowNumber = i + 2;
      const rowErrors = [];

      const reverseNumber = row[foundHeaders["Reverse Number"]]?.toString().trim();
      const numberBelowQR = row[foundHeaders["Number Below the QR"]]?.toString().trim();
      const codeQR = row[foundHeaders["Code QR"]]?.toString().trim();
      const estado = row[foundHeaders["ESTADO"]]?.toString().trim();
      const lote = row[foundHeaders["LOTE"]]?.toString().trim();
      const estadoTags = row[foundHeaders["ESTADO TAGS"]]?.toString().trim();

      if (!reverseNumber) {
        rowErrors.push("Reverse Number está vacío");
      }
      if (!numberBelowQR) {
        rowErrors.push("Number Below the QR está vacío");
      }
      if (!codeQR) {
        rowErrors.push("Code QR está vacío");
      }
      if (!estado) {
        rowErrors.push("ESTADO está vacío");
      }
      if (!lote) rowErrors.push("LOTE está vacío");
      else if (!/^\d+$/.test(lote) || !Number.isSafeInteger(Number(lote)) || Number(lote) <= 0)
        rowErrors.push("LOTE debe ser un número entero positivo");
      if (!estadoTags) {
        rowErrors.push("ESTADO TAGS está vacío");
      }

      if (reverseNumber) {
        if (reverseNumbersInFile.has(reverseNumber)) {
          duplicateInFile.push({
            row: rowNumber,
            type: "Reverse Number",
            value: reverseNumber,
            firstOccurrence: reverseNumbersInFile.get(reverseNumber),
          });
          rowErrors.push(
            `Reverse Number duplicado en el archivo (primera vez en fila ${reverseNumbersInFile.get(
              reverseNumber
            )})`
          );
        } else {
          reverseNumbersInFile.set(reverseNumber, rowNumber);
        }
      }

      if (numberBelowQR) {
        if (numbersBelowQRInFile.has(numberBelowQR)) {
          duplicateInFile.push({
            row: rowNumber,
            type: "Number Below the QR",
            value: numberBelowQR,
            firstOccurrence: numbersBelowQRInFile.get(numberBelowQR),
          });
          rowErrors.push(
            `Number Below the QR duplicado en el archivo (primera vez en fila ${numbersBelowQRInFile.get(
              numberBelowQR
            )})`
          );
        } else {
          numbersBelowQRInFile.set(numberBelowQR, rowNumber);
        }
      }

      if (codeQR) {
        if (qrCodesInFile.has(codeQR)) {
          duplicateInFile.push({
            row: rowNumber,
            type: "Code QR",
            value: codeQR,
            firstOccurrence: qrCodesInFile.get(codeQR),
          });
          rowErrors.push(
            `Code QR duplicado en el archivo (primera vez en fila ${qrCodesInFile.get(codeQR)})`
          );
        } else {
          qrCodesInFile.set(codeQR, rowNumber);
        }
      }

      if (rowErrors.length > 0) {
        errors.push({
          row: rowNumber,
          errors: rowErrors,
          data: row,
        });
      } else {
        validRows.push({
          reverse_number: reverseNumber,
          number_below_qr: numberBelowQR,
          code_qr: codeQR,
          estado: estado,
          lote: lote,
          estado_tags: estadoTags,
          originalRow: rowNumber,
        });
      }
    }

    const duplicateInDatabase = [];

    for (const row of validRows) {
      const existingCheck = {
        reverse_number: existingIndex.reverse_number.get(row.reverse_number),
        number_below_qr: existingIndex.number_below_qr.get(row.number_below_qr),
        code_qr: existingIndex.code_qr.get(row.code_qr),
      };

      const rowErrors = [];

      if (existingCheck.reverse_number) {
        duplicateInDatabase.push({
          row: row.originalRow,
          type: "Reverse Number",
          value: row.reverse_number,
          existingId: existingCheck.reverse_number.id,
        });
        rowErrors.push(`Reverse Number "${row.reverse_number}" ya existe en la base de datos`);
      }

      if (existingCheck.number_below_qr) {
        duplicateInDatabase.push({
          row: row.originalRow,
          type: "Number Below the QR",
          value: row.number_below_qr,
          existingId: existingCheck.number_below_qr.id,
        });
        rowErrors.push(
          `Number Below the QR "${row.number_below_qr}" ya existe en la base de datos`
        );
      }

      if (existingCheck.code_qr) {
        duplicateInDatabase.push({
          row: row.originalRow,
          type: "Code QR",
          value: row.code_qr,
          existingId: existingCheck.code_qr.id,
        });
        rowErrors.push(`Code QR "${row.code_qr}" ya existe en la base de datos`);
      }

      if (rowErrors.length > 0) {
        errors.push({
          row: row.originalRow,
          errors: rowErrors,
          data: row,
        });
      }
    }

    const invalidRows = new Set(errors.map((error) => error.row));
    const finalValidRows = validRows.filter((row) => !invalidRows.has(row.originalRow));

    const isValid = errors.length === 0;
    const formattedErrors = errors.map((e) => `Fila ${e.row}: ${e.errors.join(", ")}`);

    return {
      isValid,
      errors: formattedErrors,
      duplicateInFile: duplicateInFile,
      duplicateInDatabase: duplicateInDatabase,
      validRows: finalValidRows,
      totalRows: data.length,
      validCount: finalValidRows.length,
      invalidCount: errors.length,
      data: finalValidRows,
    };
  } catch (error) {
    if (!signal?.aborted) console.error("Error al validar tags:", error);
    return {
      isValid: false,
      errors: [error.message || "Error al validar el archivo"],
      duplicateInFile: [],
      duplicateInDatabase: [],
      data: [],
    };
  }
};

/**
 * Importa un archivo Excel con tags
 */
export const importTagsService = async (file, onProgress, { signal } = {}) => {
  try {
    if (!file) {
      return {
        success: false,
        error: "No se ha seleccionado ningún archivo",
      };
    }

    if (onProgress) onProgress(0);

    const validation = await validateTagsFile(file, { signal });

    if (!validation.isValid) {
      return {
        success: false,
        error: "El archivo contiene errores de validación",
        validationErrors: validation.errors,
        duplicateInFile: validation.duplicateInFile,
        duplicateInDatabase: validation.duplicateInDatabase,
      };
    }

    if (validation.validRows.length === 0) {
      return {
        success: false,
        error: "No hay filas válidas para importar",
      };
    }

    const formData = new FormData();
    // Envía los identificadores como texto también en el archivo que lee el backend.
    const workbook = workbooks.get(file);
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const range = XLSX.utils.decode_range(sheet["!ref"] || "A1");
    for (let column = range.s.c; column <= range.e.c; column += 1) {
      const header = sheet[XLSX.utils.encode_cell({ r: range.s.r, c: column })]?.v;
      if (!IDENTIFIER_HEADERS.has(header)) continue;
      for (let row = range.s.r + 1; row <= range.e.r; row += 1) {
        const address = XLSX.utils.encode_cell({ r: row, c: column });
        const cell = sheet[address];
        if (cell) sheet[address] = { t: "s", v: String(XLSX.utils.format_cell(cell)).trim() };
      }
    }
    const upload = XLSX.write(workbook, { type: "array", bookType: "xlsx" });
    formData.append(
      "file",
      new Blob([upload], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      file.name.replace(/\.(xlsx|xls)$/i, ".xlsx")
    );

    const { data } = await axiosInstance.post("/api/automatization/tag/import-tag", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: TAGS_IMPORT_TIMEOUT_MS,
      signal,
      onUploadProgress: ({ loaded, total }) => {
        if (onProgress && total > 0) onProgress(Math.min(100, Math.round((loaded * 100) / total)));
      },
    });

    if (onProgress) onProgress(100);

    if (!data || typeof data !== "object") {
      return {
        success: false,
        error: "Respuesta inválida del servidor",
      };
    }

    const isSuccess =
      data.success === true || data.status === "success" || data.msm === "succesful";
    const importedCount = Number(
      data.importedCount ?? (isSuccess ? validation.validRows.length : 0)
    );

    return {
      success: isSuccess,
      message:
        data.message ||
        (isSuccess
          ? `Se importaron ${importedCount} tags exitosamente`
          : "No se confirmó la importación"),
      error: isSuccess
        ? undefined
        : data.error || data.message || data.msm || "No se pudo importar el archivo",
      data: data.data || validation.validRows,
      importedCount: importedCount,
      validationSummary: {
        totalRows: validation.totalRows,
        validRows: validation.validCount,
        invalidRows: validation.invalidCount,
        duplicateInFile: validation.duplicateInFile.length,
        duplicateInDatabase: validation.duplicateInDatabase.length,
      },
      rawResponse: data,
    };
  } catch (error) {
    console.error("❌ Error al importar tags:", error);

    if (onProgress) onProgress(0);

    let errorMessage = "Error al importar el archivo";

    if (error.response) {
      const serverMessage =
        error.response.data?.msm || error.response.data?.message || error.response.data?.error;

      if (serverMessage) {
        errorMessage = serverMessage;
      }

      if (error.response.status === 404) {
        errorMessage = "El endpoint de importación no está disponible";
      } else if (error.response.status === 413) {
        errorMessage = "El archivo es demasiado grande para el servidor";
      } else if (error.response.status === 400) {
        errorMessage = error.response.data?.msm || "Archivo inválido o formato incorrecto";
      } else if (error.response.status === 401) {
        errorMessage = "No autorizado. Por favor, inicie sesión nuevamente.";
      } else if (error.response.status === 500) {
        errorMessage = "Error interno del servidor";
      }
    } else if (["ECONNABORTED", "ETIMEDOUT"].includes(error.code)) {
      errorMessage =
        "No se recibió confirmación de la importación en 3 minutos. Revisa el lote antes de volver a enviarlo.";
    } else if (error.request || error.code === "ERR_NETWORK") {
      errorMessage = "No se pudo conectar con el servidor";
    }

    return {
      success: false,
      error: errorMessage,
      importedCount: 0,
    };
  }
};

/**
 * Elimina un tag
 */
export const deleteTagService = async (tagId) => {
  try {
    if (!tagId) {
      return {
        success: false,
        error: "ID de tag no proporcionado",
      };
    }

    const { data } = await axiosInstance.delete(`/api/automatization/tag/delete-tag/${tagId}`);

    const isSuccess = data.success === true || data.msm === "succesful";

    return {
      success: isSuccess,
      message: data.msm || data.message || "Tag eliminado exitosamente",
      data: data.data,
    };
  } catch (error) {
    console.error("❌ Error al eliminar tag:", error);

    let errorMessage = error.message || "Error al eliminar el tag";

    if (error.response?.status === 404) {
      errorMessage = "El tag no existe o ya fue eliminado";
    } else if (error.response?.status === 401) {
      errorMessage = "No autorizado";
    } else if (error.response?.data?.msm) {
      errorMessage = error.response.data.msm;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Actualiza el estado de un tag
 */
export const updateTagStatusService = async (tagId, status) => {
  try {
    if (!tagId || !status) {
      return {
        success: false,
        error: "ID de tag o estado no proporcionado",
      };
    }

    const { data } = await axiosInstance.put(`/api/automatization/tag/update-tag/${tagId}`, {
      status,
    });

    const isSuccess = data.success === true || data.msm === "succesful";

    return {
      success: isSuccess,
      message: data.msm || data.message || "Estado actualizado exitosamente",
      data: data.data,
    };
  } catch (error) {
    console.error("❌ Error al actualizar estado del tag:", error);

    let errorMessage = error.message || "Error al actualizar el estado";

    if (error.response?.status === 404) {
      errorMessage = "El tag no existe";
    } else if (error.response?.data?.msm) {
      errorMessage = error.response.data.msm;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Obtiene tags (wrapper con paginación por defecto)
 */
export const getTagsService = async () => {
  return getTagsPaginated({ offset: 0, limit: 100 });
};

// Exportar todo como objeto para compatibilidad
export default {
  normalizeTag,
  getTagsPaginated,
  getTagsByLot,
  getTagsByStatus,
  getAllExistingTags,
  validateTagsFile,
  importTagsService,
  deleteTagService,
  updateTagStatusService,
  getTagsService,
};
